import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export interface ProgressData {
  read_pct: number;
  flash_srs: Record<string, SRSState>;
  last_quiz_score?: number;
  updated_at: string;
}

export interface SRSState {
  score: number; // 0, 1, or 2
  interval: number; // days
  next_review: string; // ISO date
  repetitions: number;
}

export interface ProgressStore {
  progress: Record<string, ProgressData>;
  updateProgress: (topicId: string, data: Partial<ProgressData>) => void;
  updateSRS: (topicId: string, cardId: string, score: number) => void;
  getProgress: (topicId: string) => ProgressData | undefined;
  getSRSState: (topicId: string, cardId: string) => SRSState | undefined;
}

export const useProgressStore = create<ProgressStore>()(
  persist(
    (set, get) => ({
      progress: {},
      
      updateProgress: (topicId: string, data: Partial<ProgressData>) => {
        set((state) => ({
          progress: {
            ...state.progress,
            [topicId]: {
              ...state.progress[topicId],
              ...data,
              updated_at: new Date().toISOString(),
            },
          },
        }));
      },
      
      updateSRS: (topicId: string, cardId: string, score: number) => {
        const state = get();
        const topicProgress = state.progress[topicId];
        const currentSRS = topicProgress?.flash_srs[cardId] || {
          score: 0,
          interval: 1,
          next_review: new Date().toISOString(),
          repetitions: 0,
        };
        
        // SRS-light algorithm
        let newInterval = currentSRS.interval;
        if (score === 0) {
          newInterval = 1; // Reset to 1 day
        } else if (score === 1) {
          newInterval = Math.max(1, Math.floor(currentSRS.interval * 0.5));
        } else if (score === 2) {
          newInterval = Math.floor(currentSRS.interval * 1.5);
        }
        
        const nextReview = new Date();
        nextReview.setDate(nextReview.getDate() + newInterval);
        
        const newSRSState: SRSState = {
          score,
          interval: newInterval,
          next_review: nextReview.toISOString(),
          repetitions: currentSRS.repetitions + 1,
        };
        
        set((state) => ({
          progress: {
            ...state.progress,
            [topicId]: {
              ...state.progress[topicId],
              flash_srs: {
                ...state.progress[topicId]?.flash_srs,
                [cardId]: newSRSState,
              },
              updated_at: new Date().toISOString(),
            },
          },
        }));
      },
      
      getProgress: (topicId: string) => {
        return get().progress[topicId];
      },
      
      getSRSState: (topicId: string, cardId: string) => {
        return get().progress[topicId]?.flash_srs[cardId];
      },
    }),
    {
      name: 'progress-storage',
    }
  )
);
